import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subject, takeUntil } from 'rxjs';
import { FleetDailySnapshot, PssAuthService, PssBattle } from '../../../services/pss-auth.service';
import { FeatureFlagService, FEATURE_FLAGS } from '../../../services/feature-flag.service';
import { HitListComponent } from '../hit-list/hit-list.component';

@Component({
  selector: 'app-battles-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, HitListComponent],
  templateUrl: './battles-tab.component.html',
  styleUrls: ['./battles-tab.component.css']
})
/**
 * ╔════════════════════════════════════════════════════════════════════════════╗
 * ║ OPPONENT TROPHY CACHE SOURCE                                              ║
 * ╠════════════════════════════════════════════════════════════════════════════╣
 * ║ Trophy counts are populated from D1 fleet snapshots, which are created by: ║
 * ║                                                                            ║
 * ║ 1. User navigates to Fleet tab                                            ║
 * ║    → fleet-tab.component calls persistFleetSnapshot(info)                  ║
 * ║    → Upserts all alliance members + their trophy counts to D1              ║
 * ║    → battles-tab can now query via getFleetSnapshots() and find them       ║
 * ║                                                                            ║
 * ║ 2. Backend periodic sync (if/when implemented)                            ║
 * ║    → Background job fetches fleet members and saves snapshot daily        ║
 * ║                                                                            ║
 * ║ Until then: opponent trophies remain hidden and unresolved.               ║
 * ║                                                                            ║
 * ║ You can safely visit the Fleet tab once to populate the snapshot DB.      ║
 * ╚════════════════════════════════════════════════════════════════════════════╝
 */
export class BattlesTabComponent implements OnChanges, OnDestroy, AfterViewInit {
  private static readonly STORAGE_KEY = 'pss_battle_filters';

  @Input() battles: PssBattle[] = [];
  @Input() loading = false;
  @Input() error = '';
  @Input() userId = '';
  @Input() allianceId = '';

  battlesTypeFilter = 'all';
  battlesSideFilter = 'all';
  battlesResultFilter = 'all';
  battlesSearch = '';
  battlesDateFrom = '';
  battlesDateTo = '';
  visibleBattlesCount = 50;
  readonly battlesPageSize = 50;
  private intersectionObserver?: IntersectionObserver;

  showBackToTop = false;
  private static readonly BACK_TO_TOP_THRESHOLD = 600;

  // Moved to document.body in ngAfterViewInit: an ancestor (.card.fade-up) leaves a
  // permanent `transform: translateY(0)` after its entry animation (fill-mode: both),
  // which makes it the containing block for `position: fixed` descendants — so without
  // this, the button anchors to the (growing) card instead of the viewport.
  @ViewChild('backToTopBtn', { static: true }) private backToTopBtnRef!: ElementRef<HTMLElement>;

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.showBackToTop = window.scrollY > BattlesTabComponent.BACK_TO_TOP_THRESHOLD;
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  ngAfterViewInit(): void {
    document.body.appendChild(this.backToTopBtnRef.nativeElement);
  }

  @ViewChild('scrollSentinel') set scrollSentinel(el: ElementRef<HTMLElement> | undefined) {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = undefined;
    if (!el) return;
    this.intersectionObserver = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) this.loadMoreBattles();
    });
    this.intersectionObserver.observe(el.nativeElement);
  }
  showHitList = false;
  showAddPlayerModal = false;
  addPlayerQuery = '';
  addPlayerLoading = false;
  addPlayerError = '';
  addPlayerSuccessMessage = '';
  addPlayerResults: Array<{ userId: string; userName: string; allianceId: string }> = [];
  manualHitListOpponentIds = new Set<string>();
  private toastTimer?: number;

  get hasHitList(): boolean {
    return this.featureFlags.isEnabled(FEATURE_FLAGS.HIT_LIST);
  }

  openHitList(): void {
    if (!this.hasHitList) return;
    this.showHitList = true;
  }

  closeHitList(): void { this.showHitList = false; }

  openAddPlayerModal(): void {
    if (!this.hasHitList) return;
    this.addPlayerQuery = '';
    this.addPlayerResults = [];
    this.addPlayerError = '';
    this.showAddPlayerModal = true;
  }

  closeAddPlayerModal(): void {
    this.showAddPlayerModal = false;
    this.addPlayerQuery = '';
    this.addPlayerResults = [];
    this.addPlayerError = '';
  }

  constructor(
    private auth: PssAuthService,
    private featureFlags: FeatureFlagService,
    private http: HttpClient,
  ) {
    this.loadFilters();
  }

  private readonly destroy$ = new Subject<void>();
  private opponentTrophies = new Map<string, number>();
  private snapshotLoadSeq = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['battles'] || changes['userId'] || changes['allianceId']) {
      this.loadOpponentTrophiesFromSnapshots();
    }
    if (changes['userId'] && this.userId) {
      this.loadManualHitList();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.intersectionObserver?.disconnect();
    this.backToTopBtnRef?.nativeElement.remove();
  }

  private loadManualHitList(): void {
    if (!this.userId) return;
    this.http.get<{ notes: { opponentId: string }[] }>(
      `/api/battles/hitlist/notes?userId=${encodeURIComponent(this.userId)}`
    ).subscribe({
      next: res => {
        this.manualHitListOpponentIds = new Set((res.notes ?? []).map(n => n.opponentId));
      },
      error: () => {}
    });
  }

  canShowAddToHitList(b: PssBattle): boolean {
    const opponentId = this.battleOpponentUserId(b);
    return !!opponentId && !this.manualHitListOpponentIds.has(opponentId);
  }

  canShowRemoveFromHitList(b: PssBattle): boolean {
    const opponentId = this.battleOpponentUserId(b);
    return !!opponentId && this.manualHitListOpponentIds.has(opponentId);
  }

  addOpponentToHitList(b: PssBattle, event: Event): void {
    event.stopPropagation();
    if (!this.hasHitList) return;
    const opponentId = this.battleOpponentUserId(b);
    if (!opponentId || !this.userId) return;
    this.http.post('/api/battles/hitlist/notes', {
      userId: this.userId,
      opponentId,
      isFavorite: false,
      myHpRemaining: null,
      opponentHpRemaining: null,
      notes: '',
      isCompleted: false
    }).subscribe({
      next: () => {
        this.manualHitListOpponentIds.add(opponentId);
        this.showToast('Player added to hit list.');
      },
      error: () => {}
    });
  }

  removeOpponentFromHitList(b: PssBattle, event: Event): void {
    event.stopPropagation();
    if (!this.hasHitList) return;
    const opponentId = this.battleOpponentUserId(b);
    if (!opponentId || !this.userId) return;
    this.http.delete(
      `/api/battles/hitlist/notes?userId=${encodeURIComponent(this.userId)}&opponentId=${encodeURIComponent(opponentId)}`
    ).subscribe({
      next: () => {
        this.manualHitListOpponentIds.delete(opponentId);
        this.showToast('Player removed from hit list.');
      },
      error: () => {}
    });
  }

  private showToast(message: string): void {
    this.addPlayerSuccessMessage = message;
    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
    }
    this.toastTimer = window.setTimeout(() => {
      this.addPlayerSuccessMessage = '';
      this.toastTimer = undefined;
    }, 3000);
  }

  searchAddPlayer(): void {
    const query = this.addPlayerQuery.trim();
    if (!query) {
      this.addPlayerResults = [];
      this.addPlayerError = '';
      return;
    }
    this.addPlayerLoading = true;
    this.addPlayerError = '';
    this.auth.searchPssUsers(query).subscribe({
      next: results => {
        this.addPlayerResults = results.map(r => ({
          userId: r.userId,
          userName: r.userName,
          allianceId: r.allianceId,
        }));
        this.addPlayerLoading = false;
      },
      error: err => {
        this.addPlayerError = err?.error?.error || 'Search failed.';
        this.addPlayerLoading = false;
      }
    });
  }

  addPlayerToHitListFromSearch(player: { userId: string; userName: string; allianceId: string }): void {
    if (!this.hasHitList || !this.userId || !player.userId) return;
    this.addPlayerLoading = true;
    this.addPlayerError = '';
    this.http.post('/api/battles/hitlist/notes', {
      userId: this.userId,
      opponentId: player.userId,
      isFavorite: false,
      myHpRemaining: null,
      opponentHpRemaining: null,
      notes: '',
      isCompleted: false
    }).subscribe({
      next: () => {
        this.addPlayerLoading = false;
        this.manualHitListOpponentIds.add(player.userId);
        this.closeAddPlayerModal();
        this.showToast('Player added to hit list.');
      },
      error: err => {
        this.addPlayerError = err?.error?.error || 'Failed to add player.';
        this.addPlayerLoading = false;
      }
    });
  }

  get extraBattleTypes(): string[] {
    const types = new Set<string>();
    this.battles.forEach(b => {
      if (b.battleType && b.battleType.toUpperCase() !== 'PVP') types.add(b.battleType);
    });
    return Array.from(types).sort();
  }

  get filteredBattles(): PssBattle[] {
    let list = this.battles;

    const restrictedModeActive = this.featureFlags.isRestrictedMode();
    if (restrictedModeActive) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 2);
      list = list.filter(b => b.battleDate && new Date(b.battleDate) >= cutoff);
    }

    if (this.battlesTypeFilter === 'pvp') {
      list = list.filter(b => b.battleType?.toUpperCase() === 'PVP' && !b.isStarBattle);
    } else if (this.battlesTypeFilter === 'tournament') {
      list = list.filter(b => b.isStarBattle);
    } else if (this.battlesTypeFilter === 'draw') {
      list = list.filter(b => this.isDraw(b));
    } else if (this.battlesTypeFilter !== 'all') {
      list = list.filter(b => b.battleType === this.battlesTypeFilter);
    }

    if (this.battlesSideFilter === 'attack') {
      list = list.filter(b => b.attackingUserId === this.userId);
    } else if (this.battlesSideFilter === 'defense') {
      list = list.filter(b => b.defendingUserId === this.userId);
    }

    if (this.battlesResultFilter === 'win') {
      list = list.filter(b => this.battleResult(b) === 'win');
    } else if (this.battlesResultFilter === 'loss') {
      list = list.filter(b => this.battleResult(b) === 'loss');
    } else if (this.battlesResultFilter === 'draw') {
      list = list.filter(b => this.battleResult(b) === 'draw');
    }

    if (this.battlesDateFrom) {
      list = list.filter(b => {
        const day = this.toUtcDayKey(b.battleDate || b.battleEndDate || '');
        return !!day && day >= this.battlesDateFrom;
      });
    }
    if (this.battlesDateTo) {
      list = list.filter(b => {
        const day = this.toUtcDayKey(b.battleDate || b.battleEndDate || '');
        return !!day && day <= this.battlesDateTo;
      });
    }

    const q = this.battlesSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(b => {
        const result = this.battleResult(b);
        const matchesResult = (q === 'win' && result === 'win')
          || (q === 'loss' && result === 'loss')
          || (q === 'draw' && result === 'draw');

        const matchesText =
          b.attackingShipName.toLowerCase().includes(q) ||
          b.defendingShipName.toLowerCase().includes(q) ||
          b.attackingAllianceName.toLowerCase().includes(q) ||
          b.defendingAllianceName.toLowerCase().includes(q);

        return matchesResult || matchesText;
      });
    }
    return list;
  }

  get battlesByDay(): Array<{ day: string; battles: PssBattle[] }> {
    const groups = new Map<string, PssBattle[]>();
    for (const b of this.pagedBattles) {
      const day = this.toUtcDayKey(b.battleDate || b.battleEndDate || '');
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day)!.push(b);
    }
    return Array.from(groups.entries())
      .map(([day, battles]) => ({ day, battles }))
      .sort((a, b) => b.day.localeCompare(a.day));
  }

  get pagedBattles(): PssBattle[] {
    return this.filteredBattles.slice(0, this.visibleBattlesCount);
  }

  get hasMoreBattles(): boolean {
    return this.visibleBattlesCount < this.filteredBattles.length;
  }

  loadMoreBattles(): void {
    if (!this.hasMoreBattles) return;
    this.visibleBattlesCount = Math.min(this.filteredBattles.length, this.visibleBattlesCount + this.battlesPageSize);
  }

  onBattlesFilterChange(): void {
    this.visibleBattlesCount = this.battlesPageSize;
    this.saveFilters();
  }

  resetBattlesFilters(): void {
    this.battlesTypeFilter = 'all';
    this.battlesSideFilter = 'all';
    this.battlesResultFilter = 'all';
    this.battlesSearch = '';
    this.battlesDateFrom = '';
    this.battlesDateTo = '';
    this.onBattlesFilterChange();
  }

  private loadFilters(): void {
    try {
      const raw = localStorage.getItem(BattlesTabComponent.STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.battlesTypeFilter) this.battlesTypeFilter = saved.battlesTypeFilter;
      if (saved.battlesSideFilter) this.battlesSideFilter = saved.battlesSideFilter;
      if (saved.battlesResultFilter) this.battlesResultFilter = saved.battlesResultFilter;
      if (saved.battlesSearch) this.battlesSearch = saved.battlesSearch;
      if (saved.battlesDateFrom) this.battlesDateFrom = saved.battlesDateFrom;
      if (saved.battlesDateTo) this.battlesDateTo = saved.battlesDateTo;
    } catch {}
  }

  private saveFilters(): void {
    try {
      localStorage.setItem(BattlesTabComponent.STORAGE_KEY, JSON.stringify({
        battlesTypeFilter: this.battlesTypeFilter,
        battlesSideFilter: this.battlesSideFilter,
        battlesResultFilter: this.battlesResultFilter,
        battlesSearch: this.battlesSearch,
        battlesDateFrom: this.battlesDateFrom,
        battlesDateTo: this.battlesDateTo
      }));
    } catch {}
  }

  battleRole(b: PssBattle): 'attack' | 'defense' {
    return b.attackingUserId === this.userId ? 'attack' : 'defense';
  }

  isDraw(b: PssBattle): boolean {
    const outcome = b.outcomeType?.toLowerCase() || '';
    return outcome.includes('draw') || outcome.includes('tie');
  }

  battleResult(b: PssBattle): 'win' | 'loss' | 'draw' | 'unknown' {
    if (!this.userId || !b.outcomeType) return 'unknown';
    if (this.isDraw(b)) return 'draw';
    const iAmAttacker = b.attackingUserId === this.userId;
    const attackerWon = b.outcomeType === 'Attacker Won';
    return (iAmAttacker === attackerWon) ? 'win' : 'loss';
  }

  battleOpponent(b: PssBattle): string {
    if (!this.userId) return '—';
    if (b.attackingUserId === this.userId) return b.defendingShipName || b.defendingUserId;
    return b.attackingShipName || b.attackingUserId;
  }

  battleOpponentFleet(b: PssBattle): string {
    if (!this.userId) return '';
    return b.attackingUserId === this.userId
      ? b.defendingAllianceName
      : b.attackingAllianceName;
  }

  battleOpponentTrophiesKnown(b: PssBattle): boolean {
    const opponentId = this.battleOpponentUserId(b);
    if (!opponentId) return false;
    const trophies = this.opponentTrophies.get(opponentId);
    return Number.isFinite(trophies);
  }

  battleOpponentTrophiesLabel(b: PssBattle): string {
    const opponentId = this.battleOpponentUserId(b);
    if (!opponentId) return '';
    const trophies = this.opponentTrophies.get(opponentId);
    return Number.isFinite(trophies) ? (trophies ?? 0).toLocaleString() : '';
  }

  battleTrophyChange(b: PssBattle): number | null {
    if (!this.userId || !b.outcomeType) return null;
    const result = this.battleResult(b);
    if (result === 'win')  return b.winTrophyResult > 0 ? b.winTrophyResult  : null;
    if (result === 'loss') {
      const isAttacker = b.attackingUserId === this.userId;
      const isLegendary = (b.leagueType || '').toLowerCase().includes('legendary');
      if (isAttacker && isLegendary) return null;
      return b.winTrophyResult > 0 ? -b.winTrophyResult : null;
    }
    return 0;
  }

  battleRewardsSummary(b: PssBattle): string {
    if (!this.userId) return '';
    const result = this.battleResult(b);
    const isAttacker = b.attackingUserId === this.userId;
    const rewards = result === 'win'
      ? (isAttacker ? b.attackerWinRewards : b.defenderWinRewards)
      : (isAttacker ? b.attackerLoseRewards : b.defenderLoseRewards);
    if (!rewards) return '';
    const parts: string[] = [];
    const m = (key: string) => { const r = new RegExp(`(?:^|\\|)${key}:(\\d+)`).exec(rewards); return r ? parseInt(r[1], 10) : 0; };
    const min = m('mineral'); if (min) parts.push(`${(min / 1000).toFixed(0)}k min`);
    const gas = m('gas');     if (gas) parts.push(`${(gas / 1000).toFixed(0)}k gas`);
    const xp  = m('xp');      if (xp)  parts.push(`${xp} xp`);
    return parts.join(' · ');
  }

  private loadOpponentTrophiesFromSnapshots(): void {
    this.opponentTrophies = new Map<string, number>();
    if (!this.userId || !this.allianceId || this.battles.length === 0) return;

    const opponentIds = this.collectOpponentUserIds();
    if (opponentIds.size === 0) return;

    const reqSeq = ++this.snapshotLoadSeq;
    this.auth.getFleetSnapshots(this.allianceId).pipe(
      takeUntil(this.destroy$)
    ).subscribe(snapshots => {
      if (reqSeq !== this.snapshotLoadSeq) return;
      const latestByUser = this.latestSnapshotByUser(snapshots, opponentIds);
      this.opponentTrophies = latestByUser;
    });
  }

  private collectOpponentUserIds(): Set<string> {
    const ids = new Set<string>();
    for (const battle of this.battles) {
      const opponentId = this.battleOpponentUserId(battle);
      if (opponentId) ids.add(opponentId);
    }
    return ids;
  }

  private battleOpponentUserId(b: PssBattle): string {
    if (!this.userId) return '';
    return b.attackingUserId === this.userId ? b.defendingUserId : b.attackingUserId;
  }

  private latestSnapshotByUser(
    snapshots: FleetDailySnapshot[],
    opponentIds: Set<string>
  ): Map<string, number> {
    const latest = new Map<string, FleetDailySnapshot>();
    for (const snap of snapshots) {
      if (!opponentIds.has(snap.userId)) continue;
      const prev = latest.get(snap.userId);
      if (!prev || this.isSnapshotNewer(snap, prev)) latest.set(snap.userId, snap);
    }
    const trophiesByUser = new Map<string, number>();
    latest.forEach((snap, userId) => trophiesByUser.set(userId, snap.trophies));
    return trophiesByUser;
  }

  todayDayKey(): string {
    return this.toUtcDayKey(new Date().toISOString());
  }

  private toUtcDayKey(value: string): string {
    const normalized = value.trim();
    if (!normalized) return '';

    // Treat timezone-agnostic ISO strings as UTC, not local time.
    const hasZone = /([zZ]|[+\-]\d\d:\d\d|[+\-]\d{4})$/.test(normalized);
    const d = new Date(hasZone ? normalized : normalized + 'Z');

    if (isNaN(d.getTime())) return '';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private isSnapshotNewer(a: FleetDailySnapshot, b: FleetDailySnapshot): boolean {
    if (a.snapshotDate !== b.snapshotDate) return a.snapshotDate > b.snapshotDate;
    if (a.recordedAt !== b.recordedAt) return a.recordedAt > b.recordedAt;
    return a.id > b.id;
  }
}
